import { useState } from "react";
import Icon from "../../../components/Icon";
import ChatText from "./ChatText";
import ImageLightbox from "./ImageLightbox";
import { formatBytes } from "./attachments";
import { initials } from "../../../utils/formatters";
import dayjs from "dayjs";

const QUICK_EMOJI = ["👍", "❤️", "😂", "🎉", "😮", "🙏"];

function timeLabel(s) {
  const d = dayjs(s);
  return d.isValid() ? d.format("h:mm A") : "";
}

export default function MessageBubble({
  message,
  isOwn,
  isDM,
  showAvatar,
  readMarker,
  onReply,
  onReact,
  onEdit,
  onDelete,
  onJumpToReply,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [reactOpen, setReactOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const deleted = message.deleted;
  const read = isOwn && isDM && readMarker && dayjs(readMarker).valueOf() >= dayjs(message.created_at).valueOf();

  const attachments = message.attachments || [];
  const images = attachments.filter((a) => a.kind === "image");
  const docs = attachments.filter((a) => a.kind !== "image");
  // A non-deleted message with neither text nor files had its media pruned.
  const mediaExpired = !deleted && !message.body && attachments.length === 0;

  const react = (emoji) => {
    onReact(message.id, emoji);
    setReactOpen(false);
  };

  return (
    <div id={`chat-msg-${message.id}`} className={`chat-msg ${isOwn ? "own" : ""} ${showAvatar ? "lead" : "cont"} ${message.pending ? "pending" : ""} ${message.failed ? "failed" : ""}`}>
      {!isOwn && (
        <div className="chat-msg-avatar">
          {showAvatar ? <div className="avatar avatar-sm">{initials(message.sender?.name)}</div> : null}
        </div>
      )}

      <div className="chat-msg-main">
        {showAvatar && !isOwn && <div className="chat-msg-name">{message.sender?.name}</div>}

        <div className="chat-bubble-wrap">
          <div className={`chat-bubble ${deleted ? "deleted" : ""} ${images.length && !message.body ? "media-only" : ""}`}>
            {message.reply_to && (
              <button type="button" className="chat-reply-quote" onClick={() => onJumpToReply?.(message.reply_to.id)}>
                <span className="chat-reply-name">{message.reply_to.sender_name || "Message"}</span>
                <span className="chat-reply-body">{message.reply_to.body || "removed message"}</span>
              </button>
            )}

            {deleted ? (
              <span className="chat-removed"><Icon name="trash" size={11} /> message removed</span>
            ) : (
              <>
                {images.length > 0 && (
                  <div className={`chat-att-images n${Math.min(images.length, 4)}`}>
                    {images.map((a) => (
                      <button
                        type="button"
                        key={a.id}
                        className="chat-att-thumb"
                        onClick={() => a.url && setLightbox(a)}
                        style={a.width && a.height && images.length === 1 ? { aspectRatio: `${a.width} / ${a.height}` } : undefined}
                      >
                        {a.url
                          ? <img src={a.url} alt={a.name} loading="lazy" />
                          : <span className="chat-att-thumb-loading"><Icon name="upload" size={16} /></span>}
                      </button>
                    ))}
                  </div>
                )}

                {docs.map((a) => (
                  a.download_url ? (
                    <a key={a.id} className="chat-att-doc" href={a.download_url} target="_blank" rel="noreferrer">
                      <span className="chat-att-doc-icon"><Icon name="file-text" size={18} /></span>
                      <span className="chat-att-doc-meta">
                        <span className="chat-att-doc-name">{a.name}</span>
                        <span className="chat-att-doc-sub">{formatBytes(a.size)}</span>
                      </span>
                      <Icon name="download" size={15} />
                    </a>
                  ) : (
                    <span key={a.id} className="chat-att-doc pending">
                      <span className="chat-att-doc-icon"><Icon name="file-text" size={18} /></span>
                      <span className="chat-att-doc-meta">
                        <span className="chat-att-doc-name">{a.name}</span>
                        <span className="chat-att-doc-sub">{formatBytes(a.size)}</span>
                      </span>
                    </span>
                  )
                ))}

                {message.body && <ChatText>{message.body}</ChatText>}
                {mediaExpired && <span className="chat-media-gone"><Icon name="clock" size={11} /> media no longer available</span>}
              </>
            )}

            <span className="chat-meta">
              {message.edited && !deleted && <span className="chat-edited">edited</span>}
              <span className="chat-time">{timeLabel(message.created_at)}</span>
              {isOwn && isDM && !deleted && (
                message.failed
                  ? <Icon name="alert-circle" size={13} className="chat-tick-failed" />
                  : message.pending
                    ? <Icon name="clock" size={12} className="chat-tick-clock" />
                    : <Icon name="check-double" size={15} className={`chat-ticks ${read ? "read" : ""}`} />
              )}
            </span>
          </div>

          {!deleted && (
            <div className="chat-msg-tools">
              <button type="button" title="React" onClick={() => setReactOpen((v) => !v)}><Icon name="message-circle" size={13} /></button>
              <button type="button" title="Reply" onClick={() => onReply(message)}><Icon name="arrow-left" size={13} /></button>
              {isOwn && (
                <button type="button" title="More" onClick={() => setMenuOpen((v) => !v)}><Icon name="more-horizontal" size={13} /></button>
              )}

              {reactOpen && (
                <div className="chat-react-pop">
                  {QUICK_EMOJI.map((e) => (
                    <button key={e} type="button" onClick={() => react(e)}>{e}</button>
                  ))}
                </div>
              )}

              {menuOpen && isOwn && (
                <div className="chat-msg-menu" onMouseLeave={() => setMenuOpen(false)}>
                  <button type="button" onClick={() => { onEdit(message); setMenuOpen(false); }}><Icon name="edit" size={12} /> Edit</button>
                  <button type="button" className="danger" onClick={() => { onDelete(message); setMenuOpen(false); }}><Icon name="trash" size={12} /> Delete</button>
                </div>
              )}
            </div>
          )}
        </div>

        {message.reactions?.length > 0 && (
          <div className="chat-reactions">
            {message.reactions.map((r) => (
              <button
                key={r.emoji}
                type="button"
                className={`chat-reaction ${r.reactedByMe ? "mine" : ""}`}
                onClick={() => onReact(message.id, r.emoji)}
              >
                <span>{r.emoji}</span><span className="chat-reaction-count">{r.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {lightbox && <ImageLightbox attachment={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
